import api from "@/lib/api";

// Release-compatibility layer (RC6). This is an ADDITIONAL release-safety check — never an
// authorisation mechanism. It decides whether THIS frontend bundle is allowed to talk to the
// backend it is pointed at, by comparing a build-time compatibility id with the backend's.
//
// States are deliberately explicit so a network outage is NEVER mistaken for a version mismatch.
export const COMPAT = {
  CHECKING: "checking",
  COMPATIBLE: "compatible",
  INCOMPATIBLE: "incompatible",
  UNREACHABLE: "unreachable",
  MISCONFIGURED: "misconfigured",
  UNKNOWN: "unknown",
};

// Build-time compatibility id (injected by CRA/craco DefinePlugin from the env at build). Read via
// a function so it is deterministic and stub-able in tests, and inlined correctly in the bundle.
export function frontendCompatibilityId() {
  return (process.env.REACT_APP_RELEASE_COMPATIBILITY_ID || "").trim() || null;
}

// Pure, deterministic classifier. `result` = { reachable: boolean, backendId: string|null }.
//
// Fail-closed ordering (RC6.1): the frontend's own id is a LOCAL, network-independent fact, so it
// is evaluated FIRST. A build produced without a compatibility id is a release misconfiguration and
// must never be excused as "offline".
export function classifyCompatibility(frontendId, result) {
  const fId = (frontendId || "").trim() || null;
  // (D) Our own build lacks a compatibility id -> release misconfiguration. Fail closed. This is
  // NOT a network outage and must never silently proceed.
  if (!fId) return COMPAT.MISCONFIGURED;
  // (A) Cannot reach the backend -> not a mismatch; preserve legitimate offline behaviour.
  if (!result || result.reachable !== true) return COMPAT.UNREACHABLE;
  const backendId = (result.backendId || "").trim() || null;
  // (E) Backend reachable but declares no/blank compatibility id -> release misconfiguration. A
  // reachable backend that omits its required release identity must never bypass the gate.
  if (!backendId) return COMPAT.MISCONFIGURED;
  // (B) match -> compatible ; (C) differ -> incompatible
  return fId === backendId ? COMPAT.COMPATIBLE : COMPAT.INCOMPATIBLE;
}

// Fetch the minimal public release doc. NEVER throws — transport failures become {reachable:false}
// so callers can distinguish "cannot reach backend" from "reached backend, ids differ".
export async function fetchBackendRelease(client = api, timeoutMs = 6000) {
  try {
    const resp = await client.get("/meta/release", { timeout: timeoutMs });
    const data = (resp && resp.data) || {};
    const backendId = data.compatibility_id != null ? String(data.compatibility_id) : null;
    return { reachable: true, backendId };
  } catch (e) {
    return { reachable: false, backendId: null };
  }
}

export async function checkCompatibility(client = api, frontendId = undefined) {
  const fid = frontendId === undefined ? frontendCompatibilityId() : frontendId;
  const result = await fetchBackendRelease(client);
  return classifyCompatibility(fid, result);
}

// A CONFIRMED release problem blocks the app: either a definitive version mismatch, or a release
// misconfiguration (missing frontend id, or a reachable backend that omits its id). CHECKING /
// COMPATIBLE / UNREACHABLE let the app run (preserving legitimate offline behaviour).
export function shouldBlock(state) {
  return state === COMPAT.INCOMPATIBLE || state === COMPAT.MISCONFIGURED;
}
